/* No dependencies, analytics, storage, cart writes or API credentials. */
(() => {
  'use strict';
  if (customElements.get('moomenn-size-guide')) return;

  class MooMennSizeGuide extends HTMLElement {
    connectedCallback() {
      if (this._abort) return;
      this._abort = new AbortController();
      const { signal } = this._abort;
      this.querySelector('[data-sg-units]')?.removeAttribute('hidden');
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-sg-unit]');
        if (!button || !this.contains(button)) return;
        this.setUnit(button.dataset.sgUnit);
      }, { signal });
      this.bindImage();
      this.setUnit(this.dataset.defaultUnit === 'in' ? 'in' : 'cm');
      /* Public hook for a verified theme adapter. No guessed global variant events. */
      this.addEventListener('mm:size-guide:image', (event) => {
        if (String(event.detail?.productId) !== this.dataset.productId) return;
        if (this.dataset.imageLocked === 'true') return; // approved image override wins
        this.updateImage(event.detail?.url, event.detail?.alt);
      }, { signal });
    }

    disconnectedCallback() {
      this._abort?.abort();
      this._abort = null;
      this._imageObserver?.disconnect();
      this._imageObserver = null;
    }

    setUnit(unit) {
      if (unit !== 'cm' && unit !== 'in') return;
      this.dataset.unit = unit;
      this.querySelectorAll('[data-sg-unit]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.sgUnit === unit));
      });
      this.querySelectorAll('[data-sg-min]').forEach((element) => {
        const min = Number(element.dataset.sgMin);
        const max = element.hasAttribute('data-sg-max') ? Number(element.dataset.sgMax) : min;
        if (!Number.isFinite(min) || !Number.isFinite(max) || min < 0 || max < min) return;
        const format = (value) => unit === 'in' ? (value / 2.54).toFixed(1) : String(Number(value.toFixed(2)));
        element.textContent = min === max ? format(min) : `${format(min)}–${format(max)}`;
      });
      this.querySelectorAll('[data-sg-unit-label]').forEach((label) => { label.textContent = unit; });
      this.querySelectorAll('[data-sg-unit-word]').forEach((label) => {
        label.textContent = unit === 'in' ? 'inches' : 'centimetres';
      });
      const status = this.querySelector('[data-sg-status]');
      if (status) status.textContent = `Measurements shown in ${unit === 'in' ? 'inches' : 'centimetres'}.`;
    }

    bindImage() {
      const image = this.querySelector('[data-sg-image]');
      if (!image) return;
      const fallback = this.querySelector('[data-sg-image-fallback]');
      if (fallback && (!image.complete || image.naturalWidth === 0)) {
        fallback.textContent = 'Loading product photo…';
        fallback.hidden = false;
      }
      // Defer image download until the modal's image region actually becomes visible.
      if ('IntersectionObserver' in window && image.loading === 'lazy') {
        this._imageObserver = new IntersectionObserver((entries) => {
          if (entries.some(entry => entry.isIntersecting)) {
            image.loading = 'eager';
            this._imageObserver.disconnect();
          }
        }, { rootMargin: '80px' });
        this._imageObserver.observe(image.parentElement);
      }
      const failed = () => {
        if (fallback) fallback.textContent = 'Product photo unavailable';
        image.hidden = true;
        this.querySelector('[data-sg-image-fallback]')?.removeAttribute('hidden');
      };
      image.addEventListener('error', failed, { signal: this._abort.signal });
      image.addEventListener('load', () => {
        image.hidden = false;
        this.querySelector('[data-sg-image-fallback]')?.setAttribute('hidden', '');
      }, { signal: this._abort.signal });
      if (image.complete && image.currentSrc && image.naturalWidth === 0) failed();
    }

    updateImage(url, alt) {
      const image = this.querySelector('[data-sg-image]');
      if (!image || typeof url !== 'string') return;
      let parsed;
      try { parsed = new URL(url, location.href); } catch { return; }
      // Only allow the shop's own origin or Shopify's public image CDN.
      if (parsed.protocol !== 'https:' ||
          (parsed.hostname !== 'cdn.shopify.com' && parsed.origin !== location.origin)) return;
      image.removeAttribute('srcset');
      image.src = parsed.href;
      if (typeof alt === 'string' && alt.trim()) image.alt = alt;
    }
  }
  customElements.define('moomenn-size-guide', MooMennSizeGuide);
})();
